import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { Info } from 'lucide-react';
import { BottomSheet, Button, Popover } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { OrderSide } from '@/analytics';
import {
  usePredictionMarketConfigResolvePriceCube,
  useTradingOrderAmendOrder,
  useTradingOrderGetOrderHook,
  useTradingOrderPlaceOrder,
} from '@/lib/endpoints';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import {
  AmendOrderDtoAction,
  AmendOrderDtoSide,
  PlaceOrderDtoAction,
  PlaceOrderDtoSide,
  PlaceOrderDtoTimeInForce,
  PlaceOrderDtoType,
  PredictionMarketConfigResolvePriceCubeFormat,
} from '@/lib/models';
import type { PlaeeOrderResponseDto } from '@/lib/models';
import { isUnfilledCancelled } from '@/lib/trading/order-status';
import { centsToPercent } from '@/lib/binary-market';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useBuyInFooter } from '@/lib/footer/useBuyInFooter';
import { useOpenTerritoryGate } from '@/store/territoryGateStore';
import { useOnboardingGate } from '@/lib/auth/useOnboardingGate';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';
import type { BuyMarketContext } from './BuyInPopup';
import { BuyKeypad } from './BuyKeypad';
import { MarketOutcomePricesSubscriber, optionCents, useOutcomePrices } from './useOutcomePrices';

export interface LimitOrderPopupProps {
  /** The option to place a limit order on, or `null` when the sheet is closed. */
  market: BuyMarketContext | null;
  /**
   * When set, the sheet MODIFIES this resting order instead of placing a new
   * one: side + expiration are fixed (Plaee amend accepts count/price only),
   * fields prefill from the order, and submit re-queues it (loses time priority).
   */
  amendOrder?: PlaeeOrderResponseDto | null;
  /** Close the sheet. */
  onClose: () => void;
}

/** Which pill field the keypad edits. */
type LimitField = 'count' | 'price';

/**
 * Expiration policies mapped to the API's `timeInForce` vocabulary
 * (GTC = good-til-cancelled, EOD = end of day, IOC = immediate-or-cancel).
 */
const EXPIRATIONS = [
  { label: 'GTC', tif: PlaceOrderDtoTimeInForce.gtc, hint: 'Your order stays active until it’s executed or canceled' },
  { label: 'EOD', tif: PlaceOrderDtoTimeInForce.day, hint: 'Your order stays active until the end of the trading day' },
  { label: 'IOC', tif: PlaceOrderDtoTimeInForce.ioc, hint: 'Fills immediately at your price or better, cancels the rest' },
] as const;

type ExpirationLabel = (typeof EXPIRATIONS)[number]['label'];

const CONTRACTS = PredictionMarketConfigResolvePriceCubeFormat.CONTRACTS;

/** How long to wait before checking whether an IOC found any fill (covers Plaee's settle lag). */
const IOC_CHECK_DELAY_MS = 2500;

/**
 * Limit-order entry sheet (Figma node 5199-128628).
 *
 * Sibling of {@link BuyInPopup} sharing the same `BottomSheet`, `BuyKeypad`,
 * quick-add cubes, gates (kill switch + territory) and failure mapping, plus the
 * limit-specific fields: Contracts, Limit Price (cents, 1–99) and Expiration.
 * Places a resting order via {@link useTradingOrderPlaceOrder} with
 * `type: 'limit'` + `count` + `price` + `timeInForce`, then refetches the
 * order + position mirrors.
 *
 * With `amendOrder` set it becomes the **Modify** sheet for a resting order:
 * count/price prefill and submit goes through the amend endpoint (side and
 * expiration are not amendable). An IOC placement is checked after a short
 * delay — placement is async-acknowledged, so the no-fill verdict only exists
 * once the mirror row lands — and a zero-fill IOC shows failure-style feedback
 * instead of silently closing.
 */
export function LimitOrderPopup({ market, amendOrder, onClose }: LimitOrderPopupProps) {
  const [side, setSide] = useState<OrderSide>('yes');
  const [countStr, setCountStr] = useState('');
  const [priceStr, setPriceStr] = useState('');
  const [activeField, setActiveField] = useState<LimitField>('count');
  const [expiration, setExpiration] = useState<ExpirationLabel>('GTC');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingIoc, setCheckingIoc] = useState(false);
  const { prices, setPrice } = useOutcomePrices();
  const queryClient = useQueryClient();
  const territory = useTerritory();
  const { data: buyInFooter } = useBuyInFooter(territory?.country?.code);
  const footerHtml = buyInFooter?.contentHtml ?? '';
  const footerClean = footerHtml ? DOMPurify.sanitize(footerHtml, { ADD_ATTR: ['target', 'rel'] }) : '';
  const openTerritoryGate = useOpenTerritoryGate();
  const ensureOrderAuth = useOnboardingGate('order_gate');
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const amendEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_AMEND);
  const placeOrder = useTradingOrderPlaceOrder();
  const amendMutation = useTradingOrderAmendOrder();
  const getOrder = useTradingOrderGetOrderHook();
  const iocTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (iocTimerRef.current) clearTimeout(iocTimerRef.current);
    },
    [],
  );

  const isAmend = Boolean(amendOrder);

  // Reset entry whenever a new option (or a different order to modify) opens the
  // sheet. Place mode seeds the limit price from the Yes side's current cents so
  // the field starts at market; amend mode prefills from the resting order.
  const [openedId, setOpenedId] = useState<string | null>(null);
  const contextId = market ? `${market.id}:${amendOrder?.clientOrderId ?? ''}` : null;
  if (market && contextId !== openedId) {
    setOpenedId(contextId);
    if (amendOrder) {
      setSide(amendOrder.contractSide);
      setCountStr(String(amendOrder.orderQty));
      setPriceStr(String(amendOrder.price));
    } else {
      setSide('yes');
      setCountStr('');
      setPriceStr(market.yes.priceCents != null ? String(market.yes.priceCents) : '');
    }
    setActiveField('count');
    setExpiration('GTC');
    setShowBreakdown(false);
    setError(null);
    setCheckingIoc(false);
  }

  const contractCubes = usePredictionMarketConfigResolvePriceCube(
    { format: CONTRACTS },
    { query: { enabled: Boolean(market) } },
  );

  const open = Boolean(market);
  if (!market) {
    return <BottomSheet open={false} onClose={onClose} />;
  }
  const activeMarket = market;

  // Live-or-seed cents drive the probability display; the typed price drives the order.
  const cents = optionCents(activeMarket, prices);
  const sideCents = side === 'yes' ? cents.yes : cents.no;
  const target = side === 'yes' ? activeMarket.yes : activeMarket.no;

  const count = parseInt(countStr, 10) || 0;
  const price = parseInt(priceStr, 10) || 0;
  const priceOk = price >= 1 && price <= 99;
  const costDollars = (count * price) / 100;
  const maxPayoutDollars = count; // each contract settles at $1.00
  const cubes = contractCubes.data?.values ?? [];

  function handleKey(key: string) {
    if (key === '.') return; // contracts and cents are both integers
    if (activeField === 'count') {
      setCountStr((prev) => (prev === '0' ? key : prev + key).slice(0, 6));
    } else {
      setPriceStr((prev) => (prev === '0' ? key : prev + key).slice(0, 2)); // 2 digits caps at 99¢
    }
  }

  function handleBackspace() {
    if (activeField === 'count') setCountStr((p) => p.slice(0, -1));
    else setPriceStr((p) => p.slice(0, -1));
  }

  function handleCube(value: number) {
    setActiveField('count');
    setCountStr((prev) => String((parseInt(prev, 10) || 0) + value));
  }

  function handleToggleSide(next: OrderSide) {
    if (next === side) return;
    setSide(next);
    // A Yes price says nothing about the No side — re-seed from its live cents.
    const nextCents = next === 'yes' ? cents.yes : cents.no;
    setPriceStr(nextCents != null ? String(nextCents) : '');
  }

  const oddsPercent = centsToPercent(sideCents) ?? 0;
  const pending = placeOrder.isPending || amendMutation.isPending || checkingIoc;
  const gateEnabled = isAmend ? tradingEnabled && amendEnabled : tradingEnabled;
  const canBuy = gateEnabled && count >= 1 && priceOk && !pending && (!isAmend || Boolean(amendOrder?.clientOrderId));
  const sideLabel = side === 'yes' ? 'Yes' : 'No';
  const countLabel = count >= 1 ? `${count.toLocaleString('en-US')} ${sideLabel}` : sideLabel;
  const buyLabel = pending ? (isAmend ? 'Updating…' : 'Placing…') : `${isAmend ? 'Update' : 'Buy'} ${countLabel}`;

  /**
   * An IOC is acknowledged as `pending` and settles async — poll the mirror once
   * after the settle lag: a cancelled order with zero fills means "nothing
   * executed" (failure-style feedback, PRD §5.3/Q4); anything else closes as a
   * normal placement.
   */
  function scheduleIocCheck(clientOrderId: string) {
    setCheckingIoc(true);
    iocTimerRef.current = setTimeout(async () => {
      try {
        const order = await getOrder(clientOrderId);
        if (isUnfilledCancelled(order)) {
          setCheckingIoc(false);
          setError('Your order found no match — nothing was executed.');
          return;
        }
      } catch {
        // Mirror row not there yet — the order was accepted; fall through and close.
      }
      setCheckingIoc(false);
      invalidateTradingCaches(queryClient);
      onClose();
    }, IOC_CHECK_DELAY_MS);
  }

  function handleSubmit() {
    if (!canBuy) return;
    // Signup-funnel resume comes before any verification gate (ARC-145).
    if (!ensureOrderAuth()) return;
    if (territory?.blocked) {
      openTerritoryGate({ reason: territory.reason });
      return;
    }
    setError(null);

    if (isAmend && amendOrder?.clientOrderId) {
      // Re-queue the resting order with the new count/price (loses time priority).
      amendMutation.mutate(
        {
          orderId: amendOrder.orderId || amendOrder.clientOrderId,
          data: {
            origClientOrderId: amendOrder.clientOrderId,
            outcomeId: amendOrder.outcomeId,
            side: amendOrder.contractSide === 'yes' ? AmendOrderDtoSide.yes : AmendOrderDtoSide.no,
            action: AmendOrderDtoAction.buy,
            count,
            price,
          },
        },
        {
          onSuccess: (result) => {
            if (!result.success) {
              setError(ORDER_FAILURE_MESSAGE[toOrderFailureReason(result.errorCode)]);
              return;
            }
            invalidateTradingCaches(queryClient);
            onClose();
          },
          onError: () => setError(ORDER_FAILURE_MESSAGE.network),
        },
      );
      return;
    }

    placeOrder.mutate(
      {
        data: {
          outcomeId: target.externalOutcomeId,
          side: target.side === 'yes' ? PlaceOrderDtoSide.yes : PlaceOrderDtoSide.no,
          action: PlaceOrderDtoAction.buy,
          type: PlaceOrderDtoType.limit,
          count,
          price,
          timeInForce: EXPIRATIONS.find((e) => e.label === expiration)?.tif,
        },
      },
      {
        onSuccess: (result) => {
          if (!result.success) {
            const reason = toOrderFailureReason(result.errorCode);
            setError(ORDER_FAILURE_MESSAGE[reason]);
            return;
          }
          if (expiration === 'IOC' && result.clientOrderId) {
            scheduleIocCheck(result.clientOrderId);
            return;
          }
          invalidateTradingCaches(queryClient);
          onClose();
        },
        onError: () => {
          setError(ORDER_FAILURE_MESSAGE.network);
        },
      },
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="w-full bg-surface max-w-[450px] mx-auto">
        <MarketOutcomePricesSubscriber
          marketId={activeMarket.marketId}
          externalMarketId={activeMarket.externalMarketId}
          onPrice={setPrice}
        />

        {/* Header */}
        <div className="mb-1 flex items-center justify-center gap-1.5">
          <span className="size-3.5 rounded-full bg-accent" aria-hidden="true" />
          <span className="text-sm font-medium text-accent">{isAmend ? 'Modify Order' : 'Limit Order'}</span>
        </div>
        <p className="mb-3 text-center text-sm font-semibold text-primary">{activeMarket.label ?? activeMarket.eventName}</p>

        {/* Contracts + Limit Price fields */}
        <div className="flex flex-col gap-2.5">
          <Field
            label="Contracts"
            value={count >= 1 ? count.toLocaleString('en-US') : ''}
            placeholder="0"
            active={activeField === 'count'}
            onSelect={() => setActiveField('count')}
          />
          <Field
            label="Limit Price"
            value={priceStr === '' ? '' : `${parseInt(priceStr, 10)}¢`}
            placeholder="1–99¢"
            active={activeField === 'price'}
            onSelect={() => setActiveField('price')}
          />
        </div>

        {/* Max Payout + cost breakdown */}
        <div className="mt-3 flex items-center justify-center gap-1.5">
          <span className="text-base text-primary">
            Max Payout{' '}
            <span className="font-semibold text-success">
              ${maxPayoutDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </span>
          <Popover
            open={showBreakdown}
            onClose={() => setShowBreakdown(false)}
            align="start"
            className="left-[calc(50%-30px)] -translate-x-1/2 w-[248px] p-4 text-sm text-primary"
            anchor={
              <button
                type="button"
                aria-label="Price breakdown"
                onClick={() => setShowBreakdown((s) => !s)}
                className="text-muted bg-transparent border-none cursor-pointer"
              >
                <Info className="size-3.5" />
              </button>
            }
          >
            <p className="mb-2 font-medium capitalize">Price Breakdown</p>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between">
                <span>Limit price</span>
                <span className="tabular-nums">{priceOk ? price : 0}¢</span>
              </div>
              <div className="flex justify-between">
                <span>Contracts</span>
                <span className="tabular-nums">{count.toLocaleString('en-US')}</span>
              </div>
              <div className="flex justify-between">
                <span>Total cost</span>
                <span className="tabular-nums">
                  ${costDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="my-2 border-t border-border" />
            <div className="flex justify-between font-medium">
              <span>Max payout</span>
              <span className="tabular-nums text-success">
                ${maxPayoutDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </Popover>
        </div>

        {/* Quick-add cubes (admin-configured contract steps) */}
        {cubes.length > 0 && (
          <div className="mt-3 mb-2 grid grid-cols-4 gap-2">
            {cubes.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleCube(value)}
                className="h-8 rounded-pill border border-border text-primary font-semibold text-xs bg-surface hover:bg-surface-muted active:opacity-70 transition-colors"
              >
                +{value}
              </button>
            ))}
          </div>
        )}

        <BuyKeypad onKey={handleKey} onBackspace={handleBackspace} />

        {/* Expiration — not amendable, so the selector only renders when placing. */}
        {!isAmend && (
          <div className="mt-3 flex flex-col items-center gap-1.5">
            <span className="self-start text-sm font-medium text-primary">Expiration</span>
            <div className="grid w-full grid-cols-3 gap-2">
              {EXPIRATIONS.map(({ label }) => {
                const selected = label === expiration;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setExpiration(label)}
                    className={cn(
                      'h-8 rounded-pill border-[1.5px] bg-surface text-sm font-semibold uppercase transition-colors',
                      selected ? 'border-accent text-accent' : 'border-border text-primary',
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-center text-xs text-muted">{EXPIRATIONS.find((e) => e.label === expiration)?.hint}</p>
          </div>
        )}

        {/* Selected side = big CTA (buys); other side = small outline pill (switches
            side). Amend mode fixes the side, so only the big CTA renders. */}
        <div className="mt-3 mb-6 flex items-center gap-3">
          {side === 'yes' ? (
            <div className="relative flex flex-1 flex-col items-center">
              <Button
                variant="success"
                className="w-full h-[42px] rounded-pill text-lg whitespace-nowrap"
                disabled={!canBuy}
                onClick={handleSubmit}
              >
                {buyLabel}
              </Button>
              <span className="absolute top-full mt-0.5 text-xs text-muted">
                Probability <span className="font-semibold">{oddsPercent}%</span>
              </span>
            </div>
          ) : (
            !isAmend && (
              <Button
                variant="outline"
                size="md"
                className="w-[120px] shrink-0 h-[36px] text-sm border-success text-success-dark"
                onClick={() => handleToggleSide('yes')}
              >
                Buy Yes
              </Button>
            )
          )}

          {side === 'no' ? (
            <div className="relative flex flex-1 flex-col items-center">
              <Button
                variant="danger"
                className="w-full h-[42px] rounded-pill text-lg whitespace-nowrap"
                disabled={!canBuy}
                onClick={handleSubmit}
              >
                {buyLabel}
              </Button>
              <span className="absolute top-full mt-0.5 text-xs text-muted">
                Probability <span className="font-semibold">{oddsPercent}%</span>
              </span>
            </div>
          ) : (
            !isAmend && (
              <Button
                variant="outline"
                size="md"
                className="w-[120px] shrink-0 h-[36px] text-sm border-danger-alt text-danger-alt"
                onClick={() => handleToggleSide('no')}
              >
                Buy No
              </Button>
            )
          )}
        </div>

        {checkingIoc && <p className="text-center text-xs font-medium text-muted">Checking for a match…</p>}
        {(error || !gateEnabled) && (
          <p className={cn('text-center text-xs font-medium', error ? 'text-danger-alt' : 'text-muted')}>
            {error ?? 'Trading is temporarily paused'}
          </p>
        )}

        {footerClean && (
          <div
            className="mt-3 text-center text-[10px] leading-snug text-muted"
            dangerouslySetInnerHTML={{ __html: footerClean }}
          />
        )}
      </div>
    </BottomSheet>
  );
}

/** One labelled, pill-shaped field the keypad edits; click to focus it. */
function Field({
  label,
  value,
  placeholder,
  active,
  onSelect,
}: {
  label: string;
  value: string;
  placeholder: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex flex-col gap-1 bg-transparent border-none p-0 text-left cursor-pointer"
    >
      <span className="text-sm font-medium text-primary">{label}</span>
      <div
        className={cn(
          'flex h-10 w-full items-center rounded-pill border-[1.5px] bg-surface-muted px-4 text-base',
          active ? 'border-accent' : 'border-border',
          value ? 'text-primary' : 'text-muted',
        )}
      >
        {value || placeholder}
      </div>
    </button>
  );
}
